import { useEffect, useMemo, useRef, useState } from "react";
import PreviewModal from "./PreviewModal";
import { asset } from "../data/assetMap";
import { UploadIcon } from "./icons";
import { inputMaxLength } from "../utils/formSchema";

// Shows just the file name for an uploaded URL (…/rem/38/1781851376905.JPEG).
const fileLabel = (v) => {
  const s = String(v);
  return /^https?:\/\//.test(s) ? decodeURIComponent(s.split("?")[0].split("/").pop()) : s;
};

const CURRENCY_SYMBOL = "₱";

// Typed text → stored value: digits only, or digits with one "." and at most
// 2 decimals when allowDecimal (commas from the formatted display drop out).
const cleanMoney = (text, allowDecimal) => {
  if (!allowDecimal) return text.replace(/\D/g, "");
  const [int, ...rest] = text.replace(/[^0-9.]/g, "").split(".");
  return rest.length ? int + "." + rest.join("").slice(0, 2) : int;
};

// Stored value → display: groups the whole-number part (1500000.5 → 1,500,000.5).
const formatMoney = (v) => {
  const s = String(v);
  if (s === "") return "";
  const [int, dec] = s.split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec === undefined ? grouped : grouped + "." + dec;
};

export default function DynamicField({ field, value, values, onChange, error, configOptions, readOnly, onUpload, showRequired }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [localPreview, setLocalPreview] = useState(null);
  const [localName, setLocalName] = useState("");
  const [localIsPdf, setLocalIsPdf] = useState(false);
  // The uploaded URL the local preview stands in for. Stored files sit in a
  // private bucket (only signed URLs render), so after an upload the thumbnail
  // keeps showing the picked file while the value is that same URL.
  const [localFor, setLocalFor] = useState(null);
  useEffect(() => () => localPreview && URL.revokeObjectURL(localPreview), [localPreview]);
  const allCities = configOptions?.city_list;
  const parentValue = field.parent_depedencies ? values?.[field.parent_depedencies] : null;
  const citiesForState = useMemo(() => {
    if (!allCities) return [];
    return parentValue ? allCities.filter((c) => c.state_id === String(parentValue)) : allCities;
  }, [allCities, parentValue]);
  if (!field.name) return null;

  // full_row (schema flag) gives a field its own line, e.g. the Contract
  // Signing radio so Days Since Approval + date share the next row.
  const wrapClass =
    "field" + (field.type === "textarea" || field.type === "image" || field.full_row ? " field--span2" : "");
  const disabled = readOnly || field.is_enable === false;
  // Read-only/disabled fields get a grey shade so they read as locked.
  const controlClass = "field__control" + (error ? " input-invalid" : "") + (disabled ? " field__control--readonly" : "");
  const val = value == null ? "" : value;

  const emit = (v) => onChange(field.name, v);

  let control;

  if (field.type === "dd" || field.type === "sdd" || field.type === "state" || field.type === "city") {
    let options;
    if (field.type === "state") {
      options = configOptions?.state_list || [];
    } else if (field.type === "city") {
      options = citiesForState;
    } else {
      options = (field.config && configOptions?.[field.config]) || field.options || [];
    }
    // A value with no matching option (e.g. the backend's 0 for "not set")
    // would make the browser show the first real option as if it were picked.
    const selectVal = options.some((o) => String(o.value) === String(val)) ? val : "";
    control = (
      <select className={controlClass + " field__control--select"} value={selectVal} onChange={(e) => emit(e.target.value)} disabled={disabled}>
        {/* Placeholder only: shown while nothing is picked, never listed as a choice. */}
        <option value="" disabled hidden>
          Select {field.label}
        </option>
        {options.map((o) => (
          <option key={o.id ?? o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    );
  } else if (field.type === "textarea") {
    control = <textarea className={controlClass} value={val} onChange={(e) => emit(e.target.value)} readOnly={disabled} />;
  } else if (field.type === "date") {
    // The native picker shouldn't even offer a date younger than min_age (or,
    // for a plain date field with no min_age, anything past today) — matches
    // the same rule validateField enforces after the fact.
    // allow_future opts a field out (e.g. NOA's approval_valid_until).
    const maxDate = new Date();
    if (field.validation?.min_age) maxDate.setFullYear(maxDate.getFullYear() - field.validation.min_age);
    control = (
      <input
        className={controlClass}
        type="date"
        value={val}
        max={field.allow_future ? undefined : maxDate.toISOString().slice(0, 10)}
        onChange={(e) => emit(e.target.value)}
        // Open the calendar from a click anywhere in the box, not just the
        // icon. showPicker() is missing on older WebViews and throws outside
        // a user gesture — typing the date still works either way.
        onClick={(e) => {
          if (disabled) return;
          try {
            e.currentTarget.showPicker?.();
          } catch (err) {
            /* fall back to the native icon */
          }
        }}
        style={disabled ? undefined : { cursor: "pointer" }}
        readOnly={disabled}
      />
    );
  } else if (field.type === "mobile" || field.type === "telephone") {
    const input = (
      <input
        className={controlClass}
        value={val}
        onChange={(e) => emit(e.target.value.replace(/\D/g, ""))}
        readOnly={disabled}
        inputMode="numeric"
        maxLength={inputMaxLength(field)}
      />
    );
    // Country code sits inside the input box, just before the typed number.
    control = field.prefix ? (
      <div className="field__prefixed">
        <span className="field__prefix">{field.prefix}</span>
        {input}
      </div>
    ) : (
      input
    );
  } else if (field.type === "currency" || field.is_currency) {
    // Money: ₱ inside the box and 1,234,567 grouping on screen, while the
    // stored value stays plain digits (validation + payloads are unchanged).
    // decimal fields flagged is_currency (NOA amounts) keep up to 2 decimals.
    const allowDecimal = field.type === "decimal";
    const input = (
      <input
        className={controlClass}
        value={formatMoney(val)}
        onChange={(e) => {
          const el = e.target;
          // Keep the caret after the same digit once commas shift around.
          const digitsBefore = el.value.slice(0, el.selectionStart).replace(/[^0-9.]/g, "").length;
          const raw = cleanMoney(el.value, allowDecimal);
          emit(raw);
          requestAnimationFrame(() => {
            if (document.activeElement !== el) return;
            const shown = el.value;
            let pos = 0;
            for (let seen = 0; pos < shown.length && seen < digitsBefore; pos++) if (/[0-9.]/.test(shown[pos])) seen++;
            el.setSelectionRange(pos, pos);
          });
        }}
        readOnly={disabled}
        inputMode={allowDecimal ? "decimal" : "numeric"}
      />
    );
    control = (
      <div className="field__prefixed field__prefixed--currency">
        <span className="field__prefix">{field.prefix || CURRENCY_SYMBOL}</span>
        {input}
      </div>
    );
  } else if (field.type === "number") {
    control = (
      <input
        className={controlClass}
        value={val}
        onChange={(e) => emit(e.target.value.replace(/\D/g, ""))}
        readOnly={disabled}
        inputMode="numeric"
      />
    );
  } else if (field.type === "decimal") {
    control = (
      <input
        className={controlClass}
        value={val}
        onChange={(e) => emit(e.target.value.replace(/[^0-9.]/g, ""))}
        readOnly={disabled}
        inputMode="decimal"
      />
    );
  } else if (field.type === "postal_code") {
    control = (
      <input
        className={controlClass}
        value={val}
        onChange={(e) => emit(e.target.value.replace(/\D/g, ""))}
        readOnly={disabled}
        inputMode="numeric"
        maxLength={field.validation?.max_length || undefined}
      />
    );
  } else if (field.type === "radio" || field.type === "toggle") {
    control = (
      <div className="yn-row">
        {(field.options || []).map((o) => (
          <button
            key={o.id ?? o.value}
            type="button"
            className="yn-option"
            disabled={disabled}
            onClick={() => emit(o.value)}
          >
            <span className="yn-circle">
              <span className={"yn-dot" + (String(val) === String(o.value) ? " yn-dot--on" : "")} />
            </span>
            {o.label}
          </button>
        ))}
      </div>
    );
  } else if (field.type === "image") {
    // While the upload is in flight the local file (blob:) is previewed, then
    // swapped for the uploaded URL once it lands.
    const keepLocal = localPreview && (uploading || (localFor && localFor === val));
    const shown = keepLocal ? localPreview : val;
    const isUrl = /^(https?:|blob:)/.test(String(shown));
    const isPdf = keepLocal ? localIsPdf : /\.pdf$/i.test(String(shown).split("?")[0]);
    const openPreview = () => (isPdf ? window.open(shown, "_blank", "noopener") : setPreviewOpen(true));
    control = (
      <div className="upload-field">
        {shown && isUrl ? (
          <div className="upload-field__done">
            {/* Thumbnail — click for the same PreviewModal the Documents step
                uses (PDFs open in a new tab). */}
            <button
              type="button"
              className={"upload-field__thumb" + (uploading ? " upload-field__thumb--busy" : "")}
              style={{ backgroundImage: `url("${isPdf ? asset("placeholder-document-doc.png") : shown}")` }}
              onClick={openPreview}
              aria-label={"Preview " + field.label}
            />
            <div className="upload-field__meta">
              <button type="button" className="upload-field__name" onClick={openPreview} title={fileLabel(shown)}>
                {(keepLocal && localName) || fileLabel(shown)}
              </button>
              <span className="upload-field__hint">{uploading ? "Uploading…" : "Click the thumbnail to preview"}</span>
              {!disabled && !uploading && (
                <button type="button" className="upload-field__replace" onClick={() => fileRef.current && fileRef.current.click()}>
                  Replace file
                </button>
              )}
            </div>
          </div>
        ) : disabled ? (
          <span className="upload-field__empty">No document uploaded</span>
        ) : (
          <button
            type="button"
            className={"upload-field__drop" + (error ? " upload-field__drop--invalid" : "")}
            disabled={uploading}
            onClick={() => fileRef.current && fileRef.current.click()}
          >
            <UploadIcon size={20} />
            <span>{uploading ? "Uploading…" : "Click to upload"}</span>
            <span className="upload-field__hint">Image or PDF</span>
          </button>
        )}
        {shown && !isUrl && <span className="upload-field__empty">{shown}</span>}
        {previewOpen && (
          <PreviewModal doc={{ name: (keepLocal && localName) || fileLabel(shown), prevUrl: shown }} onClose={() => setPreviewOpen(false)} />
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*,application/pdf"
          hidden
          onChange={(e) => {
            const file = e.target.files && e.target.files[0];
            e.target.value = "";
            if (!file) return;
            // With an uploader the stored value is the uploaded file's URL;
            // without one (forms with no save API yet) it stays the file name.
            if (!onUpload) {
              emit(file.name);
              return;
            }
            const blobUrl = URL.createObjectURL(file);
            setLocalPreview(blobUrl);
            setLocalName(file.name);
            setLocalIsPdf(file.type === "application/pdf");
            setLocalFor(null);
            setUploading(true);
            onUpload(field, file)
              .then((url) => {
                if (url) {
                  setLocalFor(url);
                  emit(url);
                } else {
                  setLocalPreview(null);
                }
              })
              .catch(() => setLocalPreview(null))
              .finally(() => setUploading(false));
          }}
        />
      </div>
    );
  } else {
    control = (
      <input
        className={controlClass}
        type={field.keyboard_type === "email-address" ? "email" : "text"}
        value={val}
        onChange={(e) => emit(e.target.value)}
        readOnly={disabled}
      />
    );
  }

  const staticLabel = field.type === "radio" || field.type === "toggle" || field.type === "image";

  return (
    <div className={wrapClass + (disabled ? " field--disabled" : "")}>
      <span className={staticLabel ? "field__label--static" : "field__label"}>
        {field.label}
        {showRequired && field.is_required && !disabled && <span className="field__req">*</span>}
      </span>
      {control}
      {error && <span className="field-error">{error}</span>}
    </div>
  );
}
